import React, { FC } from 'react';
import { useTranslate, usePermissions } from 'react-admin';
import { useSelector, useDispatch } from 'react-redux';
import { Paper } from '@material-ui/core';

import { VirtualizedList } from '~/components';
import { TextField } from '~/components';
import {
  selectors,
  getVirtualizedListBegin,
  getVirtualizedListSuccess,
  getVirtualizedListFail,
} from '~/ducks/virtualizedList';
import { getPendingApprovals } from '~/api/approvals';
import { ApprovalCase, ApprovalCaseDetail } from '~/types/approvalCase';
import { time, Longdash } from '~/utils';

const resource = 'casesForObject';
const i18nPath = 'resources.approvals.';

interface Props {
  record?: ApprovalCaseDetail;
}

const CasesForObjectTab: FC<Props> = (props) => {
  const { record } = props;
  const translate = useTranslate();
  const dispatch = useDispatch();
  const { permissions } = usePermissions();
  const withAllQueue =
    permissions?.includes('approvalCase.list.pending.all') ||
    permissions?.includes('approvalCase.admin');
  const list = useSelector(selectors.getList(resource));
  const { hasNextPage, isNextPageLoading, data } = list;

  const loadNextPage = async () => {
    if (!record) return;
    if (isNextPageLoading || (!list.cursors.next && list.data.length > 0)) return;

    const queryToken = Math.random();
    try {
      dispatch(getVirtualizedListBegin({ resource, queryToken }));

      const { data: responseItems } = await getPendingApprovals({
        next: list.cursors.next,
        filters: {
          objectType: record.objectType,
          objectId: record.objectId ?? undefined,
          ...(withAllQueue ? { withAllQueue: true } : {}),
        },
      });

      dispatch(
        getVirtualizedListSuccess({
          resource,
          responseData: {
            ...responseItems,
            cursors: responseItems.cursors,
            records: (responseItems.items ?? []) as any[],
          },
          queryToken,
        })
      );
    } catch (error) {
      dispatch(getVirtualizedListFail({ resource, error: error as Error, queryToken }));
    }
  };

  const columns = [
    {
      id: 'submittedAt',
      header: translate(i18nPath + 'list.submittedAt'),
      selectCellData: (r: ApprovalCase) => r.submittedAt,
      cellDataWrapperComponent: ({ children }: { children: string }) => {
        if (!children) return <span>{Longdash}</span>;
        const dt = time(children);
        return (
          <TextField compact label={dt.format('LL')}>
            {dt.format('LTS')}
          </TextField>
        );
      },
      style: { flexBasis: 180, minWidth: 140, maxWidth: 200 },
    },
    {
      id: 'actionName',
      header: translate(i18nPath + 'list.actionName'),
      selectCellData: (r: ApprovalCase) => r.actionName,
      cellDataWrapperComponent: ({ children }: { children: string }) => (
        <span>{children ?? Longdash}</span>
      ),
      style: { flexBasis: 300 },
    },
  ];

  const getRowLink = (r: ApprovalCase) => `/approvals/queue/${r.id}/show`;

  if (!record) return null;

  return (
    <Paper style={{ flex: '1 1 auto', overflow: 'hidden', paddingBottom: 2 }}>
      <VirtualizedList<ApprovalCase>
        resource={resource}
        columns={columns}
        hasNextPage={hasNextPage}
        isNextPageLoading={isNextPageLoading}
        filters={{ objectType: record.objectType, objectId: record.objectId }}
        data={data}
        loadNextPage={loadNextPage}
        rowHeight={48}
        getRowLink={getRowLink}
      />
    </Paper>
  );
};

export default CasesForObjectTab;
